import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TablePagination,
  CircularProgress,
  TableContainer,
  Button,
  TableCell,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  Alert,
  Snackbar,
  Tooltip,
  Chip,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { border, borderLeft, borderRight, styled } from "@mui/system";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { green, red } from "@mui/material/colors";
import axios from "axios";
import { API_URL } from "../../../Config/api";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import PendingActionsIcon from "@mui/icons-material/PendingActions";

/* ===================== STYLES ===================== */

const OngoingServiceCard = styled(Card)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "0.5fr 1fr 1fr 1fr 0.8fr 1fr 1fr 1fr 1fr 1fr 1fr 0.4fr",
  alignItems: "center",
  height: "3.2rem",

  backgroundColor: "#fff",
  borderRadius: 8,
  boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
  transition: "0.25s",
  "&:hover": {
    backgroundColor: "#F7F7F7",
    borderLeft: "4px solid #4CAF50",
    borderRight: "4px solid #4CAF50",
    boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
  },
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "1fr 1fr",
    height: "auto",
    rowGap: 8,
  },
}));

const CenteredCardContent = styled(CardContent)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  padding: "6px",
  fontSize: "13px",
});

/* ===================== TABLE CONFIG ===================== */

const TABLE_HEADERS = [
  "Sr.No",
  "Event Code",
  "Start Date",
  "End Date",
  "Session",
  "Revalidation",
  "Prof Cost",
  "Total Cost",
  "Paid Sessions",
  "Feedback",
  "Payment Taken",
  "Action",
];

/* ===================== COMPONENT ===================== */

const ViewPayoutDetailsmodal = ({ row }) => {
  const accessToken = localStorage.getItem("token");
  const [tableData, setTableData] = useState([]);

  const [selectedRows, setSelectedRows] = useState([]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [actionLoading, setActionLoading] = useState(false);

  // Dialog states
  const [openApproveDialog, setOpenApproveDialog] = useState(false);
  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const [rejectRemark, setRejectRemark] = useState("");

  const [loading, setLoading] = useState(false);

  const [approveCharges, setApproveCharges] = useState(0);
  const [policeCharges, setPoliceCharges] = useState(0);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success", // success | error | warning | info
  });

  const [statusMessage, setStatusMessage] = useState("");


const approneStatus = Number(row?.approne_charges_status);
const policeStatus = Number(row?.police_verification_charges_status);

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const summary = useMemo(() => {
    const selectedItems = selectedRows.map((i) => tableData[i]).filter(Boolean);

    const totalProfessionalCost = selectedItems.reduce(
      (sum, item) => sum + Number(item.total_prof_cost || 0),
      0,
    );

    const conveyance = selectedItems.reduce(
      (sum, item) => sum + Number(item.convenience || 0),
      0,
    );

    const tds = totalProfessionalCost * 0.1;

    const netAmount =
      totalProfessionalCost -
      tds -
      Number(approveCharges || 0) -
      Number(policeCharges || 0) +
      conveyance;

    return {
      totalProfessionalCost,
      tds,
      conveyance,
      netAmount,
    };
  }, [selectedRows, tableData, approveCharges, policeCharges]);

  const handleCheckboxToggle = (rowIndex) => {
    setSelectedRows((prev) =>
      prev.includes(rowIndex)
        ? prev.filter((i) => i !== rowIndex)
        : [...prev, rowIndex],
    );
  };

  const paginatedData = useMemo(() => {
    if (!Array.isArray(tableData)) return [];
    return tableData.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage,
    );
  }, [tableData, page, rowsPerPage]);

  const fetchDetails = async () => {
    try {
      setLoading(true);
      setStatusMessage(""); // Reset message before fetch
      const response = await axios.get(`${API_URL}/pay_out/prof_dtl_unit/`, {
        params: {
          start_date: row.start_date,
          end_date: row.end_date,
          srv_prof_ids: row.srv_prof_id,
        },
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const apiData = response.data;

      // Check if apiData is an array and has items
      if (Array.isArray(apiData) && apiData.length > 0) {
        setTableData(apiData);
      } else if (
        apiData.data &&
        Array.isArray(apiData.data) &&
        apiData.data.length > 0
      ) {
        setTableData(apiData.data);
      } else {
        setTableData([]);
        if (apiData.status) {
          setStatusMessage(apiData.status);
        }
      }
    } catch (error) {
      console.error("View API error", error);
      setTableData([]);

      if (error.response?.data?.status) {
        setStatusMessage(error.response.data.status);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!row) return;

    fetchDetails();
  }, [row]);

  const handleApprove = async () => {
    if (selectedRows.length === 0) {
      setSnackbar({
        open: true,
        message: "Please select at least one event",
        severity: "warning",
      });
      return;
    }

    setActionLoading(true);

    try {
      const selectedItems = selectedRows
        .map((i) => tableData[i])
        .filter(Boolean);

      const events = selectedItems.map((item) => ({
        eve_id: item.eve_id,
        service_start_date: item.service_start_date,
        service_end_date: item.service_end_date,
        session_count: item.total_sessions,
        revalidation_session: item.revalidated_session,
        session_paid: item.session_paid,
        prof_cost: item.prof_cost,
        total_prof_cost: item.total_prof_cost,
        feed_back_status: item.feedback || false,
        payment_with_status: item.payment_status ?? 1,
        revalidated_session_ids: item.revalidated_session_ids || [],
      }));

      const totalRequestedAmount = selectedItems.reduce(
        (sum, i) => sum + Number(i.total_prof_cost || 0),
        0,
      );

      const TDS_deducted_amount = totalRequestedAmount * 0.1;
      const convenience = selectedItems.reduce(
        (sum, i) => sum + Number(i.convenience || 0),
        0,
      );

      const payload = {
        srv_prof_id: row.srv_prof_id,
        total_requeststed_amount: totalRequestedAmount,
        TDS_deducted_amount,
        convenience,
        approne_charges: Number(approveCharges || 0),
        approne_charges_status: 2,
        police_verification_charges: Number(policeCharges || 0),
        police_verification_charges_status: 2,
        net_amount:
          totalRequestedAmount -
          TDS_deducted_amount -
          Number(approveCharges || 0) -
          Number(policeCharges || 0) +
          convenience,
        events,
      };

      await axios.post(`${API_URL}/pay_out/prof_dtl_unit_request/`, payload, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      setSnackbar({
        open: true,
        message: "Payout approved successfully",
        severity: "success",
      });

      setSelectedRows([]);
      setApproveCharges(0);
      setPoliceCharges(0);
      fetchDetails();

      setOpenApproveDialog(false);
    } catch (error) {
      console.error("Approve API error", error);
      setSnackbar({
        open: true,
        message: "Something went wrong while approving payout",
        severity: "error",
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Bank Verification Status Check
  const isBankVerified =
    row?.is_bank_verified === true ||
    row?.is_bank_verified === "true" ||
    row?.prof_bank_verification === "true" ||
    row?.prof_bank_verification === true;

  if (!row) return null;

  if (loading) {
    return (
      <Box sx={{ height: "60vh", display: "grid", placeItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {isBankVerified ? (
        <Alert
          severity="success"
          variant="outlined"
          icon={<CheckCircleIcon sx={{ color: green[600] }} />}
          sx={{
            mb: 2,
            borderRadius: 2,
            fontWeight: 600,
            backgroundColor: "#f0fdf4", // Light green background
            borderColor: green[200],
          }}
        >
          Verification Success: Professional's bank account is verified.
        </Alert>
      ) : (
        <Alert
          severity="warning"
          variant="filled"
          icon={<WarningAmberIcon fontSize="inherit" />}
          sx={{ mb: 2, borderRadius: 2, fontWeight: 600 }}
        >
          Attention: Bank details are NOT verified. Payout approval is blocked.
        </Alert>
      )}

      {/* ===================== TABLE CARD ===================== */}
      <Card sx={{ p: 2, borderRadius: 2 }}>
        <TableContainer
          sx={{
            width: "100%",
            overflowX: "auto",
            maxHeight: "50vh",
          }}
        >
          <Table
            stickyHeader
            sx={{ minWidth: 1200 }} // 👈 VERY IMPORTANT
          >
            <TableHead>
              <TableRow>
                {TABLE_HEADERS.map((h, i) => (
                  <TableCell
                    key={h}
                    align="center"
                    sx={{
                      backgroundColor: "#a6b4be",
                      fontWeight: 700,
                      color: "#000",
                      whiteSpace: "nowrap",
                      borderRight:
                        i === TABLE_HEADERS.length - 1
                          ? "none"
                          : "1px solid #fff",

                      borderTopLeftRadius: i === 0 ? 12 : 0,
                      borderTopRightRadius:
                        i === TABLE_HEADERS.length - 1 ? 12 : 0,
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.length > 0 ? (
                paginatedData.map((item, index) => {
                  const rowIndex = page * rowsPerPage + index;
                  const isChecked = selectedRows.includes(rowIndex);
                  return (
                    <TableRow
                      key={rowIndex}
                      hover
                      sx={{ backgroundColor: "#fff" }}
                    >
                      <TableCell align="center">{rowIndex + 1}</TableCell>
                      <TableCell align="center">{item.event_code}</TableCell>
                      <TableCell align="center">
                        {item.service_start_date}
                      </TableCell>
                      <TableCell align="center">
                        {item.service_end_date}
                      </TableCell>
                      <TableCell align="center">
                        {item.total_sessions}
                      </TableCell>
                      <TableCell align="center">
                        {item.revalidated_session}
                      </TableCell>
                      <TableCell align="center">₹{item.prof_cost}</TableCell>
                      <TableCell align="center">
                        ₹{item.total_prof_cost}
                      </TableCell>
                      <TableCell align="center">{item.session_paid}</TableCell>
                      <TableCell align="center">
                        <Tooltip
                          title={
                            item.feed_back_status
                              ? "Feedback Received"
                              : "Pending Feedback"
                          }
                        >
                          <span>
                            {item.feed_back_status ? (
                              <CheckCircleIcon color="success" />
                            ) : (
                              <PendingActionsIcon color="action" />
                            )}
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip
                          title={
                            item.payment_status === 1
                              ? "Payment has not been made yet"
                              : item.payment_status === 2
                                ? "Payment is currently held by the professional"
                                : item.payment_status === 3
                                  ? "Payment has been successfully submitted to the desk"
                                  : item.payment_status === 4
                                    ? "Payment marked as Complimentary"
                                    : "Unknown payment status"
                          }
                          arrow
                          placement="top"
                        >
                          <Chip
                            // Label mein ab poora message dikhega
                            label={
                              item.payment_status === 1
                                ? "Unpaid"
                                : item.payment_status === 2
                                  ? "By professional"
                                  : item.payment_status === 3
                                    ? "By Desk"
                                    : item.payment_status === 4
                                      ? "Complimentary"
                                      : "Unknown Status"
                            }
                            size="small"
                            sx={{
                              fontWeight: 600,
                              fontSize: "11px",
                              px: 1, // Thoda extra padding taaki lamba text fit ho sake
                              backgroundColor:
                                item.payment_status === 1
                                  ? "#ffebee"
                                  : item.payment_status === 2
                                    ? "#e3f2fd"
                                    : item.payment_status === 3
                                      ? "#e8f5e9"
                                      : item.payment_status === 4
                                        ? "#bbdefb"
                                        : "#f5f5f5",
                              color:
                                item.payment_status === 1
                                  ? "#c62828"
                                  : item.payment_status === 2
                                    ? "#1565c0"
                                    : item.payment_status === 3
                                      ? "#2e7d32"
                                      : item.payment_status === 4
                                        ? "#0d47a1"
                                        : "#616161",

                              borderColor:
                                item.payment_status === 1
                                  ? "#ffcdd2"
                                  : item.payment_status === 2
                                    ? "#bbdefb"
                                    : item.payment_status === 3
                                      ? "#c8e6c9"
                                      : item.payment_status === 4
                                        ? "#bbdefb"
                                        : "#e0e0e0",

                              maxWidth: "180px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          size="small"
                          checked={isChecked}
                          onChange={() => handleCheckboxToggle(rowIndex)}
                          color="success"
                        />
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                /* JAB DATA NAHI HOGA TAB YE ROW DIKHEGI */
                <TableRow>
                  <TableCell
                    colSpan={TABLE_HEADERS.length}
                    align="center"
                    sx={{ py: 5 }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{ color: "#666", fontWeight: 500 }}
                      >
                        {statusMessage ||
                          "No records found for the selected criteria."}
                      </Typography>
                      {statusMessage && (
                        <Typography variant="caption" color="textSecondary">
                          System Status: Action not required or already
                          processed.
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          sx={{
            overflow: "hidden",
            "& .MuiTablePagination-toolbar": { paddingRight: 1 },
          }}
          component="div"
          count={tableData.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(+e.target.value);
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </Card>

      {/* ===================== SUMMARY ===================== */}
      <AnimatePresence>
        {selectedRows.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            <Card sx={{ mt: 3, borderRadius: 2, background: "#FAFAFA" }}>
              <Table
                size="small"
                sx={{
                  "& th, & td": { textAlign: "center", fontSize: 13 },
                  "& th": { fontWeight: 700, backgroundColor: "#F1F8E9" },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>Total Professional Cost</TableCell>
                    <TableCell>TDS (10%)</TableCell>
                    <TableCell>Conveyance</TableCell>
                    {/* <TableCell>Approne</TableCell>
                    <TableCell>Police Verification</TableCell> */}
                    <TableCell>Net Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      ₹{summary.totalProfessionalCost.toFixed(2)}
                    </TableCell>
                    <TableCell>₹{summary.tds.toFixed(2)}</TableCell>
                    <TableCell>₹{summary.conveyance.toFixed(2)}</TableCell>
                    {/* <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={approveCharges}
                        onChange={(e) => setApproveCharges(e.target.value)}
                        disabled={selectedRows.some(
                          (index) =>
                            Number(tableData[index]?.approne_charges_status) ===
                            1,
                        )}
                       
                      />
                    </TableCell> */}
                    {/* <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={policeCharges}
                        onChange={(e) => setPoliceCharges(e.target.value)}
                        disabled={selectedRows.some(
                          (index) =>
                            Number(
                              tableData[index]
                                ?.police_verification_charges_status,
                            ) === 1,
                        )}  
                        
                      />
                    </TableCell> */}
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        color: summary.netAmount <= 0 ? "#d32f2f" : "#2E7D32",
                      }}
                    >
                      ₹{summary.netAmount.toFixed(2)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ===================== ACTION BAR ===================== */}
      {/* Ise top par import zaroori hai: import { Tooltip } from "@mui/material"; */}

      {/* ===================== ACTION BAR ===================== */}
      <Box
        sx={{
          mt: 3,
          p: 2,
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          borderTop: "1px dashed #CFCFCF",
        }}
      >
        <Tooltip
          title={
            !isBankVerified
              ? "Bank details not verified. Payout blocked."
              : selectedRows.length === 0
                ? "Please select at least one event to approve."
                : summary.netAmount <= 0
                  ? `Net Amount (₹${summary.netAmount.toFixed(2)}) must be greater than zero.`
                  : ""
          }
          arrow
          placement="top"
        >
          {/* Span wrapper zaroori hai kyunki disabled element par tooltip trigger nahi hota */}
          <span>
            <Button
              variant="contained"
              color="success"
              // 👇 Ye wo line hai jo button ko disable karti hai
              disabled={
                actionLoading ||
                selectedRows.length === 0 ||
                !isBankVerified ||
                summary.netAmount <= 0
              }
              onClick={() => setOpenApproveDialog(true)}
            >
              Approve
            </Button>
          </span>
        </Tooltip>
      </Box>

      {/* ===================== DIALOGS ===================== */}
      <Dialog
        open={openApproveDialog}
        onClose={() => setOpenApproveDialog(false)}
      >
        <DialogTitle>Confirm Approval</DialogTitle>
        <DialogContent>
          Are you sure you want to approve this payout?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenApproveDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleApprove}
            disabled={actionLoading}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* <Dialog
        open={openRejectDialog}
        onClose={() => setOpenRejectDialog(false)}
      >
        <DialogTitle>Reject Payout</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Remark"
            fullWidth
            multiline
            minRows={3}
            value={rejectRemark}
            onChange={(e) => setRejectRemark(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRejectDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReject}
            disabled={actionLoading}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog> */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ViewPayoutDetailsmodal;
